import type {
  DestinationHistoryItem,
  DocumentHistoryItem,
  DocumentSearchMode,
  DocumentTab,
  FragmentHighlight,
  RegionHighlight,
  SearchHistoryItem,
  SearchStateUpdate,
  TimeSeriesHistoryItem,
} from 'features/knowledgeDocumentViewer/types'
import { SelectedTab } from 'features/navbar/types'
import {
  CLEAR_SEARCH_VIEWER_PARAMS,
  type DocumentsSearchParams,
  urlSearchParamsFromColumnFilters,
} from 'routes/documents/-searchParams'

import { parseSelectedRegionsParam } from './selectedRegionsParam'

export const CLEAR_DOCUMENT_PARAMS: DocumentsSearchParams = {
  documentID: undefined,
  pageNumber: undefined,
  selectedBoxes: undefined,
  selectedRegions: undefined,
  searchFilterVendor: undefined,
  searchFilterDocType: undefined,
}

export const CLEAR_ALL_VIEWER_PARAMS: DocumentsSearchParams = {
  ...CLEAR_DOCUMENT_PARAMS,
  ...CLEAR_SEARCH_VIEWER_PARAMS,
}

const DEFAULT_SUBMITTED_DOCUMENT_SEARCH_MODE: DocumentSearchMode = 'agentic'

/**
 * Maps search tab state to URL search params (document keys must be cleared separately).
 */
export function searchParamsFromSearchState(
  searchState: SearchHistoryItem['searchState']
): Pick<
  DocumentsSearchParams,
  | 'searchQuery'
  | 'searchInputQuery'
  | 'searchFilterTitle'
  | 'searchFilterDocumentTitle'
  | 'searchFilterSystem'
  | 'searchFilterDrawing'
  | 'searchFilterVendor'
  | 'searchFilterDocType'
  | 'searchTab'
  | 'searchBrowseAll'
> {
  const inputQuery =
    searchState.inputQuery !== undefined &&
    searchState.inputQuery !== searchState.query
      ? searchState.inputQuery
      : undefined

  return {
    searchQuery: searchState.query || undefined,
    searchInputQuery: inputQuery,
    ...urlSearchParamsFromColumnFilters(searchState.filters),
    searchTab: searchState.contentFilter === 'sensors' ? 'sensors' : undefined,
    searchBrowseAll: searchState.browseAll === true ? true : undefined,
  }
}

/**
 * Applies a partial update to the current search history item for `tabId`.
 * When that tab is active, returns `urlSync` so the caller can mirror merged state to the URL.
 */
export function mergeSearchStateIntoTabs(
  prevTabs: DocumentTab[],
  tabId: string,
  activeTabId: string,
  searchStateUpdates: SearchStateUpdate
): { nextTabs: DocumentTab[]; urlSync?: SearchHistoryItem['searchState'] } {
  let urlSync: SearchHistoryItem['searchState'] | undefined
  const nextTabs = prevTabs.map((tab) => {
    if (tab.id !== tabId) return tab
    const currentItem = tab.history[tab.currentIndex]
    if (currentItem.type !== 'search') return tab
    const updates =
      typeof searchStateUpdates === 'function'
        ? searchStateUpdates(currentItem.searchState)
        : searchStateUpdates
    const merged: SearchHistoryItem['searchState'] = {
      ...currentItem.searchState,
      ...updates,
    }
    const shouldSyncUrl = Object.keys(updates).some(
      (key) => key !== 'inputQuery'
    )
    if (tabId === activeTabId && shouldSyncUrl) {
      urlSync = merged
    }
    const updatedItem: SearchHistoryItem = {
      ...currentItem,
      searchState: merged,
    }
    const newHistory = [...tab.history]
    newHistory[tab.currentIndex] = updatedItem
    return { ...tab, history: newHistory }
  })
  return { nextTabs, urlSync }
}

/**
 * Merges a URL-derived search patch into the active tab’s current search history item.
 * When the active tab shows something else (e.g. a persisted document tab), the
 * deep-linked search params still win the foreground: they open in a fresh
 * search tab instead of being dropped and overwritten by the tab's URL sync.
 */
export function applySearchUrlPatchToTabs(
  tabs: DocumentTab[],
  activeId: string,
  patch: Partial<SearchHistoryItem['searchState']>
): { openTabs: DocumentTab[]; activeTabId: string } {
  const tab = tabs.find((t) => t.id === activeId) ?? tabs[0]
  if (!tab) return { openTabs: tabs, activeTabId: activeId }
  const item = tab.history[tab.currentIndex]
  if (item.type !== 'search') {
    const freshItem = createDefaultSearchHistoryItem()
    const searchTab: DocumentTab = {
      ...createDefaultTab(),
      history: [
        {
          ...freshItem,
          searchState: { ...freshItem.searchState, ...patch },
        },
      ],
    }
    return { openTabs: [...tabs, searchTab], activeTabId: searchTab.id }
  }
  const updatedItem: SearchHistoryItem = {
    ...item,
    searchState: { ...item.searchState, ...patch },
  }
  const newHistory = [...tab.history]
  newHistory[tab.currentIndex] = updatedItem
  return {
    openTabs: tabs.map((t) =>
      t.id === tab.id ? { ...t, history: newHistory } : t
    ),
    activeTabId: tab.id,
  }
}

export const createDefaultSearchHistoryItem = (): SearchHistoryItem => ({
  type: 'search',
  searchState: {
    contentFilter: 'documents',
    searchMode: DEFAULT_SUBMITTED_DOCUMENT_SEARCH_MODE,
    viewMode: 'card',
    query: '',
    filters: [],
    sorting: [],
    pagination: { pageIndex: 0, pageSize: 200 },
    selectedDocumentId: null,
  },
})

export const createDefaultTab = (id?: string): DocumentTab => ({
  id: id ?? `new-tab-${crypto.randomUUID()}`,
  history: [createDefaultSearchHistoryItem()],
  currentIndex: 0,
})

export const createWorkspaceHomeTab = (): DocumentTab => ({
  ...createDefaultTab(),
  workspaceDestination: SelectedTab.Home,
})

export const createBrowseAllDocumentsTab = (): DocumentTab => {
  const searchItem = createDefaultSearchHistoryItem()
  return {
    id: `documents-tab-${crypto.randomUUID()}`,
    history: [
      {
        ...searchItem,
        searchState: {
          ...searchItem.searchState,
          searchMode: 'normal',
          browseAll: true,
        },
      },
    ],
    currentIndex: 0,
    workspaceDestination: SelectedTab.Documents,
  }
}

export const createDestinationTab = ({
  destinationKey,
  label,
  href,
}: Omit<DestinationHistoryItem, 'type'>): DocumentTab => ({
  id: `destination-tab-${crypto.randomUUID()}`,
  history: [{ type: 'destination', destinationKey, label, href }],
  currentIndex: 0,
})

export const createDocumentTab = (
  documentId: string,
  pageNumber: number = 1,
  selectedBoxIds: string[] = [],
  query: string = '',
  fragmentHighlight?: FragmentHighlight,
  searchOriginQuery?: string,
  regionHighlight?: RegionHighlight
): DocumentTab => {
  const docItem: DocumentHistoryItem = {
    type: 'document',
    documentId,
    pageNumber,
    selectedBoxIds,
    query,
    ...(searchOriginQuery && { searchOriginQuery }),
    ...(fragmentHighlight && { fragmentHighlight }),
    ...(regionHighlight && { regionHighlight }),
  }
  return {
    id: `tab-${crypto.randomUUID()}`,
    history: [docItem],
    currentIndex: 0,
  }
}

export const createTimeSeriesTab = (
  sensorIds: string[],
  label: string,
  timeWindow?: { from: string; to: string },
  autoOpenTagSearch?: boolean,
  options?: {
    sensorColors?: Record<string, string>
    deemphasizedSensorIds?: string[]
    controlled?: boolean
    /** Set false to pin the tab (non-closable, non-draggable, left-most). */
    closable?: boolean
  }
): DocumentTab => {
  const timeSeriesItem: TimeSeriesHistoryItem = {
    type: 'timeseries',
    sensorIds,
    label,
    timeWindow,
    autoOpenTagSearch,
    sensorColors: options?.sensorColors,
    deemphasizedSensorIds: options?.deemphasizedSensorIds,
    controlled: options?.controlled,
  }
  return {
    id: `timeseries-tab-${crypto.randomUUID()}`,
    history: [timeSeriesItem],
    currentIndex: 0,
    closable: options?.closable,
  }
}

/**
 * Tab backed by a SAVED chart: renders the saved chart with full fidelity via
 * the chart resolve flow (pens, colors, y-axis modes, date config) instead of
 * sensor seeding. The tab id is deterministic per chart so `addTab` focuses an
 * already-open tab for the same chart instead of duplicating it.
 */
export const createSavedChartTab = (
  chartId: string,
  label: string
): DocumentTab => {
  const chartItem: TimeSeriesHistoryItem = {
    type: 'timeseries',
    sensorIds: [],
    label,
    chartID: chartId,
  }
  return {
    id: `timeseries-tab-chart-${chartId}`,
    history: [chartItem],
    currentIndex: 0,
  }
}

/**
 * Find an open tab whose CURRENT history item is a time-series item backed by
 * `chartId`. Matches on the chart id rather than the tab id because a
 * sensor-seeded tab upgraded in place by its first save keeps its original
 * `timeseries-tab-<uuid>` id — the deterministic-id dedup in `addTab` cannot
 * see it, and opening the same saved chart again would mount a second live
 * editor that silently fights the first one's saves.
 */
export const findOpenTabForChart = (
  openTabs: DocumentTab[],
  chartId: string
): DocumentTab | undefined =>
  openTabs.find((tab) => {
    const item = tab.history[tab.currentIndex]
    return item?.type === 'timeseries' && item.chartID === chartId
  })

export const createInitialTab = (
  documentID?: string,
  pageNumber?: number,
  selectedBoxes?: string[],
  selectedRegions?: string
): DocumentTab => {
  if (documentID) {
    const regionBoxes = parseSelectedRegionsParam(selectedRegions)
    const docItem: DocumentHistoryItem = {
      type: 'document',
      documentId: documentID,
      pageNumber: pageNumber ?? 1,
      selectedBoxIds: selectedBoxes ?? [],
      query: '',
      ...(regionBoxes.length > 0 && {
        regionHighlight: { pageNum: pageNumber ?? 1, boxes: regionBoxes },
      }),
    }
    return {
      id: `tab-${crypto.randomUUID()}`,
      history: [createDefaultSearchHistoryItem(), docItem],
      currentIndex: 1,
    }
  }
  return createDefaultTab()
}
